import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaDiscord, FaSteam } from "react-icons/fa";
import { AiFillInstagram } from "react-icons/ai";
import Logo1 from "./Logo1.png";
import {
    DISCORD_URL,
    INSTAGRAM_URL,
    STEAM_JOURNEY_TO_ECHO,
    //WEBSITE_URL,
} from "../../constants/links";
import { useSiteNavigation } from "../../hooks/useSiteNavigation";
import { createRafListener, scrollToTopSmooth } from "../../utils/scroll";

const DiscordIcon = FaDiscord as unknown as React.FC<{ className?: string }>;
const InstagramIcon = AiFillInstagram as unknown as React.FC<{ className?: string }>;
//const GlobeIcon = FaGlobe as unknown as React.FC<{ className?: string }>;
const SteamIcon = FaSteam as unknown as React.FC<{ className?: string }>;

const SOCIAL_LINKS = [
    { label: "Discord", href: DISCORD_URL, Icon: DiscordIcon },
    { label: "Instagram", href: INSTAGRAM_URL, Icon: InstagramIcon },
    // { label: "Website", href: WEBSITE_URL, Icon: GlobeIcon },
    { label: "Journey to Echo on Steam", href: STEAM_JOURNEY_TO_ECHO, Icon: SteamIcon },
] as const;

const NAV_LINK_BASE =
    "relative text-[10px] font-bold uppercase tracking-[0.14em] text-[#F3E8D6] transition-colors duration-200 hover:text-[#B48B57]";

const NAV_LINK_ACTIVE =
    "after:absolute after:left-1/2 after:top-full after:mt-[11px] after:h-[1px] after:w-8 after:-translate-x-1/2 after:bg-[#9A7A4F]";

const MOBILE_LINK_BASE =
    "rounded-xl px-4 py-3 text-left text-[12px] font-black uppercase tracking-[0.16em] text-[#F3E8D6] transition hover:bg-white/[0.06]";

const SOCIAL_BUTTON_CLASS =
    "group flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-[#F3E8D6] transition duration-200 hover:border-[#9A7A4F]/40 hover:bg-white/[0.08] hover:text-[#B48B57]";

const SOCIAL_ICON_CLASS =
    "h-[15px] w-[15px] transition duration-200 group-hover:scale-110";

const HeaderComponent: React.FC = () => {
    const { location, navigate, scrollToId } = useSiteNavigation();
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const onScroll = createRafListener(() => {
            setIsScrolled(window.scrollY > 12);
        });

        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const closeMobile = useCallback(() => setIsMobileOpen(false), []);

    const goHomeTop = useCallback(() => {
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(scrollToTopSmooth, 60);
        } else {
            scrollToTopSmooth();
        }
        closeMobile();
    }, [closeMobile, location.pathname, navigate]);

    const handleNavClick = useCallback(
        (action: () => void) => {
            action();
            closeMobile();
        },
        [closeMobile]
    );

    const isHome = location.pathname === "/";
    const isGames = location.pathname === "/games";

    return (
        <header
            className={`sticky top-0 z-50 border-b border-white/10 bg-black/95 backdrop-blur-xl transition-all duration-300 ${
                isScrolled ? "shadow-[0_12px_40px_rgba(0,0,0,0.45)]" : ""
            }`}
        >
            <nav
                className={`mx-auto flex max-w-[1920px] items-center justify-between px-4 transition-all duration-300 sm:px-6 lg:px-[40px] ${
                    isScrolled ? "h-[74px]" : "h-[92px] sm:h-[102px]"
                }`}
            >
                <button
                    type="button"
                    onClick={goHomeTop}
                    className="group flex min-w-0 items-center gap-3 text-left sm:gap-4"
                >
                    <img
                        src={Logo1}
                        alt="Northruins logo"
                        className={`shrink-0 origin-center scale-[1.18] object-contain transition-all duration-300 sm:scale-[1.22] ${
                            isScrolled
                                ? "h-[48px] w-[48px] sm:h-[54px] sm:w-[54px]"
                                : "h-[58px] w-[58px] sm:h-[72px] sm:w-[72px]"
                        }`}
                    />

                    <div className="min-w-0 leading-none">
                        <p
                            className={`truncate font-black uppercase tracking-[0.16em] text-[#F3E8D6] transition-all duration-300 group-hover:text-white ${
                                isScrolled
                                    ? "text-[20px] sm:text-[22px]"
                                    : "text-[22px] sm:text-[24px]"
                            }`}
                        >
                            Northruins
                        </p>

                        <p
                            className={`mt-[6px] truncate font-semibold uppercase tracking-[0.26em] text-[#9A7A4F] transition-all duration-300 ${
                                isScrolled
                                    ? "text-[8px] opacity-80 sm:text-[9px]"
                                    : "text-[9px] sm:text-[10px]"
                            }`}
                        >
                            Game Development Studio
                        </p>
                    </div>
                </button>

                <div className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-9 md:flex lg:gap-[46px]">
                    <Link
                        to="/"
                        onClick={() => handleNavClick(scrollToTopSmooth)}
                        className={`${NAV_LINK_BASE} ${isHome ? NAV_LINK_ACTIVE : ""}`}
                    >
                        Home
                    </Link>

                    <Link
                        to="/games"
                        onClick={() => handleNavClick(scrollToTopSmooth)}
                        className={`${NAV_LINK_BASE} ${isGames ? NAV_LINK_ACTIVE : ""}`}
                    >
                        Games
                    </Link>

                    <button
                        type="button"
                        onClick={() => handleNavClick(() => scrollToId("about"))}
                        className={NAV_LINK_BASE}
                    >
                        About
                    </button>
                </div>

                <div className="hidden items-center gap-3 sm:flex">
                    {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                        <a
                            key={label}
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={label}
                            className={SOCIAL_BUTTON_CLASS}
                        >
                            <Icon className={SOCIAL_ICON_CLASS} />
                        </a>
                    ))}
                </div>

                <button
                    type="button"
                    onClick={() => setIsMobileOpen((prev) => !prev)}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-[#F3E8D6] transition hover:border-[#9A7A4F]/40 hover:bg-white/[0.08] md:hidden"
                    aria-label="Toggle navigation"
                >
                    <div className="relative h-4 w-4">
                        <span
                            className={`absolute left-0 top-1/2 h-[2px] w-4 -translate-y-1/2 rounded-full bg-current transition-transform duration-300 ${
                                isMobileOpen ? "rotate-45" : "-translate-y-[5px]"
                            }`}
                        />
                        <span
                            className={`absolute left-0 top-1/2 h-[2px] w-4 -translate-y-1/2 rounded-full bg-current transition-transform duration-300 ${
                                isMobileOpen ? "-rotate-45" : "translate-y-[5px]"
                            }`}
                        />
                    </div>
                </button>
            </nav>

            <div
                className={`overflow-hidden border-t border-white/10 bg-black/98 backdrop-blur-xl transition-all duration-300 md:hidden ${
                    isMobileOpen ? "max-h-[480px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <div className="mx-auto max-w-[1920px] px-4 py-4 sm:px-6">
                    <div className="grid gap-2">
                        <Link
                            to="/"
                            onClick={() => handleNavClick(scrollToTopSmooth)}
                            className={MOBILE_LINK_BASE}
                        >
                            Home
                        </Link>

                        <Link
                            to="/games"
                            onClick={() => handleNavClick(scrollToTopSmooth)}
                            className={MOBILE_LINK_BASE}
                        >
                            Games
                        </Link>

                        <button
                            type="button"
                            onClick={() => handleNavClick(() => scrollToId("about"))}
                            className={MOBILE_LINK_BASE}
                        >
                            About
                        </button>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
                        {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                            <a
                                key={label}
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={label}
                                className={SOCIAL_BUTTON_CLASS}
                            >
                                <Icon className="h-[15px] w-[15px]" />
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default React.memo(HeaderComponent);
